/**
 * DecisionViewModeToggle — przełącznik „Lista / Karty" kokpitu decyzji.
 *
 * Wygląda i nazywa się tak jak wbudowany przełącznik w pasku `ConfigurableTable` (te same klasy,
 * tooltipy `tenant.listBuilder.viewMode.*`, `data-testid` `<prefiks>-viewmode[-<tryb>]`), ale stoi
 * poza tabelą: wybór trybu jest JEDEN dla zwykłej listy i dla trybu sekcji kartotek, więc trzyma go
 * zakładka „Moje czekające" i przekazuje obu (tabela w trybie sterowanym chowa swój przełącznik).
 * Tryby tylko dwa — Kanban nie ma sensu dla decyzji (jak `allowedViewModes` na liście).
 *
 * Dostępność: grupa ma nazwę „Widok", a każdy przycisk nazwę trybu („Lista" / „Karty") — samo `title`
 * nie jest nazwą dostępną przycisku z samą ikoną, więc czytnik ekranu nie wiedziałby, co przełącza.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewMode } from '@saas/shared';
import { LayoutGrid, List as ListIcon, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/utils';

/** Klucze tooltipów wypisane WPROST — klucz sklejany w locie jest niewidoczny dla bramki tłumaczeń. */
const MODES: ReadonlyArray<{ mode: ViewMode; Icon: LucideIcon; titleKey: string }> = [
  { mode: 'list', Icon: ListIcon, titleKey: 'tenant.listBuilder.viewMode.list' },
  { mode: 'cards', Icon: LayoutGrid, titleKey: 'tenant.listBuilder.viewMode.cards' },
];

export interface DecisionViewModeToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  /** Prefiks `data-testid` (`<prefiks>-viewmode`, `<prefiks>-viewmode-<tryb>`). */
  testIdPrefix: string;
}

export function DecisionViewModeToggle({
  value,
  onChange,
  testIdPrefix,
}: DecisionViewModeToggleProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="inline-flex rounded-md border border-border-primary bg-bg-elevated p-0.5"
      role="group"
      aria-label={t('tenant.listBuilder.viewMode.groupLabel')}
      data-testid={`${testIdPrefix}-viewmode`}
    >
      {MODES.map(({ mode, Icon, titleKey }) => {
        const active = value === mode;
        return (
          <Button
            key={mode}
            variant="ghost"
            size="sm"
            onClick={() => onChange(mode)}
            aria-pressed={active}
            aria-label={t(titleKey)}
            title={t(titleKey)}
            data-testid={`${testIdPrefix}-viewmode-${mode}`}
            // Wymiary i kolory jak wbudowany przełącznik tabeli (ikona w małym kwadracie, aktywny
            // tryb w akcentowym tle); `Button` dokłada tylko ramkę fokusu.
            className={cn(
              'h-auto w-auto gap-0 rounded p-1.5 font-normal',
              active
                ? 'bg-accent-100 text-accent-700 hover:bg-accent-100 active:bg-accent-100 dark:bg-accent-900/40 dark:text-accent-300'
                : 'text-text-muted hover:bg-transparent hover:text-text-secondary active:bg-transparent',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </Button>
        );
      })}
    </div>
  );
}
